import {
  getIsBundleBbJsw,
  getIsBundleDevops,
  ProductKey
} from '@atlassiansox/bxpkit-core';
import _ from 'lodash';
import {
  GetEditionLabelsOptionsType,
  GetEditionLabelsReturnType,
  GetSideInfoHeaderOptionsType,
  GetTrialDurationSiteLabelOptionsType
} from '../model';
import {
  BITBUCKET,
  BUNDLE_LABEL_KEY,
  DEVELOPER_MODE,
  DEVOPS,
  FIRST_IMPRESSION_JIRA_SOFTWARE_FEATURE_LIST,
  FREE_EDITION_LABEL_KEY,
  JIRA_CORE,
  JIRA_PRODUCT_DISCOVERY,
  JSD,
  JSW,
  OPSGENIE,
  SIGNUP_OFFERING_LABEL_KEY_PREFIX,
  SIGNUP_SIDE_INFO_HEADER_PREFIX,
  STATUSPAGE,
  TRIAL_DURATION_LABEL_KEY_EXISTING_SITE,
  TRIAL_DURATION_LABEL_KEY_NEW_SITE,
  WAC_SIGNUP_FORM_KEY_BASE
} from './constants';
import { getProductNameFromKey } from './product-data';
import { BundleData } from './types';

/**
 * Label for side-info header (if applicable)
 */

export const getSideInfoHeader = ({
  labels,
  productKeys,
  developerMode
}: GetSideInfoHeaderOptionsType): string => {
  let contentKey = SIGNUP_SIDE_INFO_HEADER_PREFIX;

  if (getIsBundleBbJsw(productKeys)) {
    contentKey += BUNDLE_LABEL_KEY + JSW + BITBUCKET;
  }

  if (getIsBundleDevops(productKeys)) {
    contentKey += BUNDLE_LABEL_KEY + DEVOPS;
  }

  if (developerMode) {
    contentKey += BUNDLE_LABEL_KEY + DEVELOPER_MODE;
  }

  return labels[contentKey];
};

/**
 * Edition Labels, eg. 'Cloud Free', 'Cloud Premium'
 */

export const getEditionLabels = ({
  productKey,
  edition: originalEdition
}: GetEditionLabelsOptionsType): GetEditionLabelsReturnType => {
  let editionLabel = `Cloud ${_.capitalize(
    originalEdition || FREE_EDITION_LABEL_KEY
  )}`;
  let featuresListEditionLabelKey = originalEdition || FREE_EDITION_LABEL_KEY;

  if (getProductNameFromKey(productKey) === BITBUCKET) {
    editionLabel = `Cloud ${_.capitalize(FREE_EDITION_LABEL_KEY)}`;
    featuresListEditionLabelKey = FREE_EDITION_LABEL_KEY;
  }

  if (getProductNameFromKey(productKey) === STATUSPAGE) {
    editionLabel = '';
  }

  return { editionLabel, featuresListEditionLabelKey };
};

/**
 * Trial duration text, eg. '1-month free cloud trial',
 * for existing sites
 *
 * Returns empty string for BB & Statuspage
 */

export const getTrialDurationExistingSiteLabel = ({
  productKey,
  labels
}: GetTrialDurationSiteLabelOptionsType): string => {
  const productName = getProductNameFromKey(productKey);
  return productName === BITBUCKET || productName === STATUSPAGE
    ? ''
    : labels[TRIAL_DURATION_LABEL_KEY_EXISTING_SITE];
};

/**
 * Trial duration text, eg. '7-day free cloud trial',
 * for new sites
 *
 * Returns empty string for BB & Statuspage
 */

export const getTrialDurationNewSiteLabel = ({
  productKey,
  labels
}: GetTrialDurationSiteLabelOptionsType): string => {
  const productName = getProductNameFromKey(productKey);
  return productName === BITBUCKET || productName === STATUSPAGE
    ? ''
    : labels[
        TRIAL_DURATION_LABEL_KEY_NEW_SITE +
          (productName === OPSGENIE ? OPSGENIE : '')
      ];
};

/**
 * Get list of features, eg. side-info <li/>'s
 */

export type Labels = { [key: string]: string };

export type MigrationDataType = {
  readonly migrationSourceUuid: string;
  readonly migrationUserLimit: number;
  readonly migrationUserType: string;
  readonly migrationProductName: string;
  readonly migrationSites: string[];
  readonly invalidMigration: boolean;
  readonly claimedMigration: boolean;
};

export type GetFeaturesListOptions = {
  readonly labels: Labels;
  readonly bundleData: BundleData;
  readonly featuresListEditionLabelKey: string;
  readonly migrationData?: MigrationDataType;
  readonly developerMode?: boolean;
};

export const getFeaturesList = ({
  labels,
  bundleData,
  featuresListEditionLabelKey,
  migrationData,
  developerMode
}: GetFeaturesListOptions): string => {
  const isBundle = bundleData.products.length > 1;
  const edition = _.capitalize(featuresListEditionLabelKey);
  const includesJiraServiceManagement = _.includes(
    _.map(
      bundleData.products,
      (product) => product.productKey === ProductKey.JIRA_SERVICE_DESK
    ),
    true
  );

  if (migrationData && migrationData.migrationSourceUuid) {
    const migrationFeatureList =
      labels[`${WAC_SIGNUP_FORM_KEY_BASE}migrationFeatureList`] || '';
    if (
      migrationData.migrationUserLimit &&
      migrationData.migrationUserType &&
      migrationData.migrationProductName
    ) {
      return migrationFeatureList
        .replace('{productName}', migrationData.migrationProductName)
        .replace('{usageLimit}', migrationData.migrationUserLimit.toString())
        .replace('{userType}', migrationData.migrationUserType);
    }
    return migrationFeatureList;
  }

  const primaryProductKey = bundleData.products[0].productKey;
  let productNameFromKey = getProductNameFromKey(primaryProductKey);

  // JSD Standalone
  if (primaryProductKey.includes(ProductKey.JIRA_SERVICE_DESK)) {
    productNameFromKey = JSD;
  }

  // Jira Core Standalone
  if (primaryProductKey.includes(ProductKey.JIRA_CORE)) {
    productNameFromKey = JIRA_CORE;
  }

  // Jira Software Standalone
  if (primaryProductKey.includes(ProductKey.JIRA_SOFTWARE)) {
    productNameFromKey = FIRST_IMPRESSION_JIRA_SOFTWARE_FEATURE_LIST;
  }

  // Free bundles that include JSD
  if (
    isBundle &&
    includesJiraServiceManagement &&
    edition === FREE_EDITION_LABEL_KEY
  ) {
    productNameFromKey = BUNDLE_LABEL_KEY + JSD;
  }

  // jira-software-bitbucket
  if (bundleData.bundleName === 'jira-software-bitbucket') {
    productNameFromKey = BUNDLE_LABEL_KEY + JSW + BITBUCKET;
  }

  // devops
  if (bundleData.bundleName === 'devops') {
    productNameFromKey = BUNDLE_LABEL_KEY + DEVOPS;
  }

  if (developerMode) {
    productNameFromKey = BUNDLE_LABEL_KEY + DEVELOPER_MODE;
  }

  // team central
  if (primaryProductKey.includes(ProductKey.TEAM_CENTRAL)) {
    productNameFromKey = 'TeamCentral';
  }

  // Jira Product Discovery
  if (primaryProductKey.includes(ProductKey.JIRA_PRODUCT_DISCOVERY)) {
    productNameFromKey = JIRA_PRODUCT_DISCOVERY;
  }

  return labels[
    SIGNUP_OFFERING_LABEL_KEY_PREFIX + edition + productNameFromKey
  ];
};
